'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Sparkles,
  ChevronDown,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Message {
  id: string;
  role: 'bot' | 'user';
  text: string;
  timestamp: Date;
}

interface QuickReply {
  label: string;
  value: string;
}

/* ------------------------------------------------------------------ */
/*  Bot knowledge base – simple keyword→response map                   */
/* ------------------------------------------------------------------ */

const BOT_NAME = 'Elavarasi';

const QUICK_REPLIES: QuickReply[] = [
  { label: '🛒 Track my order', value: 'track order' },
  { label: '📦 Bulk orders', value: 'bulk orders' },
  { label: '🎁 Corporate gifts', value: 'corporate gifts' },
  { label: '✈️ Export & shipping', value: 'export shipping' },
  { label: '📞 Contact support', value: 'contact support' },
];

function getBotResponse(input: string): string {
  const lower = input.toLowerCase();

  if (lower.includes('track') || lower.includes('order status') || lower.includes('where is my order')) {
    return 'You can track your order by clicking the 📦 icon in the top navigation bar, or visit our Order Tracking page. Need your order ID? Check your email confirmation!';
  }
  if (lower.includes('bulk') || lower.includes('wholesale') || lower.includes('5kg')) {
    return 'We offer wholesale pricing for orders of 5kg and above! Visit our Bulk Orders page for tier pricing, or fill out a quick RFQ form and our team will get back to you within 24 hours. 📦';
  }
  if (lower.includes('corporate') || lower.includes('hamper') || lower.includes('gift')) {
    return 'Our corporate gifting range includes executive hampers, custom logo branding, and festival collections. Visit the Corporate Gifting page or contact us for a personalized quote! 🎁';
  }
  if (lower.includes('export') || lower.includes('shipping') || lower.includes('international') || lower.includes('abroad')) {
    return 'We ship to USA, UK, UAE, Singapore & more via temperature-controlled air freight. All products are FSSAI certified. Visit our Export page for shipping rates and delivery timelines! ✈️';
  }
  if (lower.includes('contact') || lower.includes('phone') || lower.includes('email') || lower.includes('support')) {
    return 'You can reach us at:\n📧 orders@elavarasifoods.com\n📞 +91 98765 43210\n🕐 Mon–Sat, 9 AM – 7 PM IST\n\nOr use the Contact form in our Help menu!';
  }
  if (lower.includes('mysore pak') || lower.includes('halwa') || lower.includes('murukku') || lower.includes('laddu') || lower.includes('sweet')) {
    return 'Our sweets are handcrafted with pure ghee using traditional recipes passed down through generations. 🍯 Browse our full catalog on the homepage or explore by category!';
  }
  if (lower.includes('price') || lower.includes('cost') || lower.includes('rate')) {
    return 'Prices vary by product and quantity. You can see all retail prices on our product pages. For bulk/wholesale pricing (5kg+), please visit the Bulk Orders page or request a custom quote!';
  }
  if (lower.includes('payment') || lower.includes('pay') || lower.includes('upi') || lower.includes('card')) {
    return 'We accept UPI, all major credit/debit cards, net banking, and wallet payments. Cash on delivery is available for select pin codes. All transactions are 100% secure! 🔒';
  }
  if (lower.includes('return') || lower.includes('refund') || lower.includes('cancel')) {
    return 'We have a hassle-free return policy. If your order arrives damaged, contact us within 24 hours with photos and we\'ll arrange a replacement or full refund. 🔄';
  }
  if (lower.includes('hello') || lower.includes('hi') || lower.includes('hey') || lower.includes('good')) {
    return 'Vanakkam! 🙏 Welcome to Elavarasi Foods. How can I help you today? You can ask about our products, orders, bulk pricing, or corporate gifting!';
  }

  return 'Thanks for your message! I can help with order tracking, bulk orders, corporate gifts, export shipping, and more. Try one of the quick options below, or type your question and our team will follow up! 😊';
}

import { useCommerce } from '@/context/CommerceContext';

export default function ChatBot() {
  const {
    isCartOpen,
    isCheckoutOpen,
    isTrackingOpen,
    isInvoiceOpen,
    isAccountOpen,
    isRfqOpen,
    isContactOpen,
  } = useCommerce();

  const isModalOrDrawerOpen = Boolean(
    isCartOpen ||
    isCheckoutOpen ||
    isTrackingOpen ||
    isInvoiceOpen ||
    isAccountOpen ||
    isRfqOpen ||
    isContactOpen
  );

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /* Auto-scroll to bottom on new messages */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  /* Focus input when chat opens */
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
      setHasUnread(false);
    }
  }, [isOpen]);

  /* Welcome message on first open */
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setIsTyping(true);
      setTimeout(() => {
        setMessages([
          {
            id: 'welcome',
            role: 'bot',
            text: 'Vanakkam! 🙏 I\'m your Elavarasi Foods assistant. I can help you with orders, bulk pricing, corporate gifts, and more. How can I help you today?',
            timestamp: new Date(),
          },
        ]);
        setIsTyping(false);
      }, 800);
    }
  }, [isOpen, messages.length]);

  /* Show unread badge after 5 seconds if chat never opened */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) setHasUnread(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const sendMessage = useCallback(
    (text: string) => {
      if (!text.trim()) return;

      const userMsg: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        text: text.trim(),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setInputValue('');
      setIsTyping(true);

      /* Simulate bot thinking delay */
      const delay = 600 + Math.random() * 800;
      setTimeout(() => {
        const botMsg: Message = {
          id: `bot-${Date.now()}`,
          role: 'bot',
          text: getBotResponse(text),
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMsg]);
        setIsTyping(false);
      }, delay);
    },
    [],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuickReply = (reply: QuickReply) => {
    sendMessage(reply.value);
  };

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  if (isModalOrDrawerOpen) return null;

  return (
    <>
      {/* ── Floating action button ─────────────────────────────── */}
      <motion.button
        id="chatbot-fab"
        aria-label="Open chat assistant"
        onClick={() => setIsOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[9999] flex items-center justify-center rounded-full shadow-2xl focus:outline-none"
        style={{
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, #950E09 0%, #D4451A 100%)',
          boxShadow: '0 8px 32px rgba(149, 14, 9, 0.45)',
        }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 18 }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={28} color="#fff" />
            </motion.span>
          ) : (
            <motion.span
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageCircle size={28} color="#fff" />
            </motion.span>
          )}
        </AnimatePresence>

        {/* Unread badge */}
        <AnimatePresence>
          {hasUnread && !isOpen && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#F59E0B] text-[10px] font-bold text-white shadow-lg"
            >
              1
            </motion.span>
          )}
        </AnimatePresence>

        {/* Pulse ring */}
        {!isOpen && (
          <span className="absolute inset-0 animate-ping rounded-full opacity-20" style={{ background: '#950E09' }} />
        )}
      </motion.button>

      {/* ── Chat window ────────────────────────────────────────── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="chatbot-window"
            initial={{ opacity: 0, y: 24, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 380, damping: 26 }}
            className="fixed bottom-24 right-6 z-[9999] flex flex-col overflow-hidden rounded-2xl shadow-2xl"
            style={{
              width: 380,
              maxWidth: 'calc(100vw - 32px)',
              height: 540,
              maxHeight: 'calc(100vh - 140px)',
              boxShadow: '0 24px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(149,14,9,0.08)',
            }}
          >
            {/* ── Header ──────────────────────────────────────── */}
            <div
              className="relative flex items-center gap-3 px-5 py-4"
              style={{
                background: 'linear-gradient(135deg, #950E09 0%, #B91C18 50%, #D4451A 100%)',
              }}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm">
                <Bot size={22} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-white tracking-wide">{BOT_NAME} Assistant</h3>
                <div className="flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[11px] text-white/70 font-medium">Online now</span>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/80 hover:bg-white/20 hover:text-white transition-colors"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>

              {/* Decorative dots */}
              <div className="absolute bottom-0 left-0 right-0 h-4 overflow-hidden">
                <svg className="w-full" viewBox="0 0 380 16" fill="none" preserveAspectRatio="none">
                  <path d="M0 16 Q95 0 190 16 Q285 0 380 16 L380 16 L0 16Z" fill="#FDFAF6" />
                </svg>
              </div>
            </div>

            {/* ── Messages area ───────────────────────────────── */}
            <div
              className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
              style={{ background: '#FDFAF6' }}
            >
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'bot' && (
                    <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#950E09] to-[#D4451A]">
                      <Sparkles size={14} className="text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-line ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-br from-[#950E09] to-[#B91C18] text-white rounded-br-md'
                        : 'bg-white text-[#2D2A24] shadow-sm border border-[#F0E6D6] rounded-bl-md'
                    }`}
                  >
                    {msg.text}
                  </div>
                  {msg.role === 'user' && (
                    <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F0E6D6]">
                      <User size={14} className="text-[#6B5E4F]" />
                    </div>
                  )}
                </motion.div>
              ))}

              {/* Typing indicator */}
              <AnimatePresence>
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-2"
                  >
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#950E09] to-[#D4451A]">
                      <Sparkles size={14} className="text-white" />
                    </div>
                    <div className="flex gap-1 rounded-2xl bg-white px-4 py-3 shadow-sm border border-[#F0E6D6]">
                      <span className="inline-block h-2 w-2 rounded-full bg-[#C4A97D] animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="inline-block h-2 w-2 rounded-full bg-[#C4A97D] animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="inline-block h-2 w-2 rounded-full bg-[#C4A97D] animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div ref={messagesEndRef} />
            </div>

            {/* ── Quick replies ────────────────────────────────── */}
            {messages.length <= 1 && !isTyping && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap gap-1.5 px-4 py-2 border-t border-[#F0E6D6]"
                style={{ background: '#FDFAF6' }}
              >
                {QUICK_REPLIES.map((qr) => (
                  <button
                    key={qr.value}
                    onClick={() => handleQuickReply(qr)}
                    className="rounded-full border border-[#E8D5B8] bg-white px-3 py-1.5 text-[12px] font-medium text-[#6B5E4F] transition-all hover:bg-[#950E09] hover:text-white hover:border-[#950E09] hover:shadow-md active:scale-95"
                  >
                    {qr.label}
                  </button>
                ))}
              </motion.div>
            )}

            {/* ── Input bar ───────────────────────────────────── */}
            <form
              onSubmit={handleSubmit}
              className="flex items-center gap-2 border-t border-[#F0E6D6] bg-white px-4 py-3"
            >
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type a message…"
                className="flex-1 rounded-xl border border-[#E8D5B8] bg-[#FDFAF6] px-4 py-2.5 text-[13.5px] text-[#2D2A24] placeholder:text-[#B5A68E] outline-none transition-all focus:border-[#950E09] focus:ring-2 focus:ring-[#950E09]/10"
              />
              <motion.button
                type="submit"
                disabled={!inputValue.trim()}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.92 }}
                className="flex h-10 w-10 items-center justify-center rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: inputValue.trim()
                    ? 'linear-gradient(135deg, #950E09 0%, #D4451A 100%)'
                    : '#E8D5B8',
                }}
              >
                <Send size={18} className="text-white" />
              </motion.button>
            </form>

            {/* ── Powered-by footer ───────────────────────────── */}
            <div className="bg-white px-4 py-1.5 text-center">
              <span className="text-[10px] text-[#B5A68E]">Powered by Elavarasi Foods</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
